import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";

function IdentificationTypeForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const [formData, setFormData] = useState({
    identification_type_name: "",
    min_length: "",
    max_length: "",
    description: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit" || mode === "view");
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    if ((mode !== "edit" && mode !== "view") || !id || !org?.organization_id)
      return;

    const getDataById = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/person-identification-type/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const data = response.data;
        setFormData({
          identification_type_name: data.identification_type_name ?? "",
          min_length: data.min_length ?? "",
          max_length: data.max_length ?? "",
          description: data.description ?? "",
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load identification type");
      } finally {
        setLoading(false);
      }
    };

    getDataById();
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numFields = ["min_length", "max_length"];
    setFormData((prev) => ({
      ...prev,
      [name]:
        numFields.includes(name) ? (value === "" ? "" : Number(value)) : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.identification_type_name?.trim())
      errors.identification_type_name =
        "Identification type name is required.";

    const minLen = Number(formData.min_length);
    if (formData.min_length === "" || formData.min_length == null)
      errors.min_length = "Min length is required.";
    else if (!Number.isInteger(minLen) || minLen < 0)
      errors.min_length = "Min length must be a non-negative integer.";

    const maxLen = Number(formData.max_length);
    if (formData.max_length === "" || formData.max_length == null)
      errors.max_length = "Max length is required.";
    else if (!Number.isInteger(maxLen) || maxLen < 0)
      errors.max_length = "Max length must be a non-negative integer.";

    if (
      Number.isInteger(minLen) &&
      Number.isInteger(maxLen) &&
      minLen > maxLen
    ) {
      errors.max_length =
        "Max length must be greater than or equal to min length.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getPayload = () => ({
    identification_type_name: formData.identification_type_name.trim(),
    min_length: Number(formData.min_length),
    max_length: Number(formData.max_length),
    description: formData.description?.trim() || null,
  });

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setBtnLoading(true);
    try {
      const payload = getPayload();

      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/organizations/${org.organization_id}/person-identification-type/${id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success("Identification type updated!");
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org.organization_id}/person-identification-type`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success("Identification type created!");
      }
      setFormErrors({});
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const raw = err.response.data.errors || {};
        const normalized = {};
        Object.keys(raw).forEach((key) => {
          const val = raw[key];
          normalized[key] = Array.isArray(val) ? val[0] : val;
        });
        setFormErrors(normalized);
        const firstMessage = Object.values(normalized)[0];
        toast.error(firstMessage || "Validation failed.");
      } else if (err.response?.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      } else {
        toast.error(
          err.response?.data?.message || "Something went wrong."
        );
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    
      <Header
        mode={mode}
        updateMessage={" Identification Type"}
        addMessage={" Identification Type"}
        homeLink={"/organization-configration/person-identification-type"}
        homeText={" Identification Types"}
        onSubmit={handleSubmit}
        loading={loading}
        btnLoading={btnLoading}>
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
               
                  <TextField
                    fullWidth
                    label="Identification Type Name"
                    name="identification_type_name"
                    value={formData.identification_type_name}
                    onChange={handleChange}
                    disabled={mode === "view"}
                    error={!!formErrors.identification_type_name}
                    helperText={formErrors.identification_type_name}
                    required
                    inputProps={{ maxLength: 50 }}
                  />
            
              
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Length"
                    name="min_length"
                    value={formData.min_length}
                    onChange={handleChange}
                    disabled={mode === "view"}
                    error={!!formErrors.min_length}
                    helperText={formErrors.min_length}
                    required
                    inputProps={{ min: 0, step: 1 }}
                  />
             
               
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Length"
                    name="max_length"
                    value={formData.max_length}
                    onChange={handleChange}
                    disabled={mode === "view"}
                    error={!!formErrors.max_length}
                    helperText={formErrors.max_length}
                    required
                    inputProps={{ min: 0, step: 1 }}
                  />
               
                
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    disabled={mode === "view"}
                    error={!!formErrors.description}
                    helperText={formErrors.description}
                    multiline
                    rows={2}
                  />
              
              </Grid>

              <Grid container spacing={2} mt={2}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleSubmit}
                    disabled={loading || btnLoading || mode === "view"}
                    sx={{
                      borderRadius: 2,
                      minWidth: 120,
                      textTransform: "capitalize",
                      fontWeight: 500,
                    }}
                  >
                    {btnLoading ? (
                      <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Grid>
                {mode === "edit" && (
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      onClick={() => navigate(-1)}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120,
                        textTransform: "capitalize",
                        fontWeight: 500,
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default IdentificationTypeForm;
